import { TIMEFRAME_LABELS } from 'constants/chart'
import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { TimeFrame } from 'types/analytics'
import { AnalyticsChartSeries, ChartSeriesPoint, SeriesByTimeframe } from 'utils/analytics/analyticsChartSeries'

import { ChartSkeleton } from '../Skeletons'
import BarChart from './BarChart'
import LineChart from './LineChart'

interface ChartsProps {
  series?: AnalyticsChartSeries
  isLoading?: boolean
  contextKey?: string
}

const EMPTY_SERIES: SeriesByTimeframe = {
  D: [],
  W: [],
  M: [],
}

const computeSeriesChange = (series?: { value: number }[]) => {
  if (!series || series.length < 2) return undefined
  const latest = Number(series[series.length - 1]?.value ?? 0)
  const previous = Number(series[series.length - 2]?.value ?? 0)
  if (!Number.isFinite(latest) || !Number.isFinite(previous) || previous === 0) return undefined
  return ((latest - previous) / Math.abs(previous)) * 100
}

type ChartPanelBaseProps = {
  contextKey?: string
  defaultValue?: number
  defaultTimestamp?: number
}

type TvlChartPanelProps = ChartPanelBaseProps & {
  seriesByTimeframe: SeriesByTimeframe
}

type MetricBarChartPanelProps = ChartPanelBaseProps & {
  title: string
  seriesByTimeframe: SeriesByTimeframe
}

const TVLChartPanel = memo(function TVLChartPanel({
  contextKey,
  seriesByTimeframe,
  defaultValue,
  defaultTimestamp,
}: TvlChartPanelProps) {
  const [timeframe, setTimeframe] = useState<TimeFrame>('D')
  useEffect(() => {
    setTimeframe('D')
  }, [contextKey, defaultTimestamp])

  const activeSeries = seriesByTimeframe[timeframe] ?? []
  const changePercent = computeSeriesChange(activeSeries)

  const summaryProps = useMemo(
    () => ({
      title: 'TVL',
      changePercent,
    }),
    [changePercent]
  )

  return (
    <LineChart
      data={activeSeries}
      height={220}
      className="min-w-0 min-h-[270px] lg:min-h-[332px]"
      resetKey={contextKey}
      showTimeSelector
      selectedTimeframe={timeframe}
      onTimeframeChange={setTimeframe}
      summaryProps={summaryProps}
    />
  )
})

const MetricBarChartPanel = memo(function MetricBarChartPanel({
  contextKey,
  title,
  seriesByTimeframe,
  defaultValue,
  defaultTimestamp,
}: MetricBarChartPanelProps) {
  const [timeframe, setTimeframe] = useState<TimeFrame>('D')
  useEffect(() => {
    setTimeframe('D')
  }, [contextKey, defaultTimestamp])

  const activeSeries = seriesByTimeframe[timeframe] ?? []
  const changePercent = computeSeriesChange(activeSeries)

  const summaryProps = useMemo(
    () => ({
      title,
      timeframeLabel: TIMEFRAME_LABELS[timeframe],
      changePercent,
    }),
    [title, timeframe, changePercent]
  )

  return (
    <BarChart
      data={activeSeries}
      height={220}
      className="min-w-0 min-h-[270px] lg:min-h-[332px]"
      resetKey={contextKey}
      summaryProps={summaryProps}
      defaultValue={defaultValue}
      isAnimationActive={false}
      showTimeSelector
      selectedTimeframe={timeframe}
      onTimeframeChange={(next) => setTimeframe(next as TimeFrame)}
    />
  )
})

export default function Charts({ series, isLoading: _isLoading = false, contextKey }: ChartsProps) {
  const computeHasSeries = useCallback(
    (candidate?: AnalyticsChartSeries) =>
      Boolean(candidate && (candidate.tvl.D.length || candidate.volume.D.length || candidate.fees.D.length)),
    []
  )

  const resolvedSeries = useMemo(() => {
    if (!series) return undefined

    const clonePoints = (points?: ChartSeriesPoint[]) =>
      Array.isArray(points) ? points.map((point) => ({ ...point })) : []

    const cloneSeriesByTimeframe = (source?: SeriesByTimeframe): SeriesByTimeframe => ({
      D: clonePoints(source?.D),
      W: clonePoints(source?.W),
      M: clonePoints(source?.M),
    })

    return {
      ...series,
      latestEntry: series.latestEntry ? { ...series.latestEntry } : undefined,
      tvl: cloneSeriesByTimeframe(series.tvl),
      volume: cloneSeriesByTimeframe(series.volume),
      fees: cloneSeriesByTimeframe(series.fees),
    }
  }, [series])
  const hasSeries = computeHasSeries(resolvedSeries)

  const skeletonGrid = (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,0.9fr)_repeat(2,minmax(0,1.05fr))]">
      <ChartSkeleton className="min-w-0 min-h-[270px] lg:min-h-[332px]" />
      <ChartSkeleton className="min-w-0 min-h-[270px] lg:min-h-[332px]" />
      <ChartSkeleton className="min-w-0 min-h-[270px] lg:min-h-[332px]" />
    </div>
  )

  if (!resolvedSeries || !hasSeries) {
    return skeletonGrid
  }

  const defaultValue = resolvedSeries.latestEntry

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      <TVLChartPanel
        contextKey={contextKey}
        seriesByTimeframe={resolvedSeries.tvl ?? EMPTY_SERIES}
        defaultValue={defaultValue?.tvlUSD}
        defaultTimestamp={defaultValue?.date}
      />

      <MetricBarChartPanel
        title="Volume"
        contextKey={contextKey}
        seriesByTimeframe={resolvedSeries.volume ?? EMPTY_SERIES}
        defaultValue={defaultValue?.volumeUSD}
        defaultTimestamp={defaultValue?.date}
      />

      <MetricBarChartPanel
        title="Fees"
        contextKey={contextKey}
        seriesByTimeframe={resolvedSeries.fees ?? EMPTY_SERIES}
        defaultValue={defaultValue?.feesUSD}
        defaultTimestamp={defaultValue?.date}
      />
    </div>
  )
}
